
import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { ActionCardProps } from '@/types/action-card';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from "@/components/ui/card";
import { ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActionCard } from './ActionCard';

export const NestedCard = ({ data, className }: ActionCardProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const nestedCards = data.nestedCards || [];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ 
        type: "spring",
        stiffness: 500,
        damping: 30
      }}
      className={cn(
        "relative",
        "shadow-lg hover:shadow-xl transition-all duration-300",
        className
      )}
    >
      <Card className={cn(
        "backdrop-blur-sm bg-white/10 dark:bg-gray-900/20",
        "border border-white/20 dark:border-gray-800/30",
        "bg-gradient-to-br from-primary/10 to-primary/5",
        "p-4"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-white/10 dark:border-gray-800/50">
          <div className="flex items-center gap-3">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 dark:bg-gray-800/30"
            >
              <Layers className="h-4 w-4 text-primary" />
            </motion.div>
            <div className="space-y-0.5">
              <h3 className="text-lg font-semibold tracking-tight">{data.title}</h3>
              <p className="text-sm text-muted-foreground">{data.summary}</p>
            </div>
          </div>
          
          {data.isCollapsible && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/10 dark:hover:bg-gray-800/50 transition-colors"
              aria-label={isCollapsed ? "Expand card" : "Collapse card"}
            >
              {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          )}
        </div>

        {/* Content */}
        <AnimatePresence>
          {!isCollapsed && nestedCards.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              {/* Tabs to organize nested content */}
              {nestedCards.length > 1 ? (
                <div className="pt-4">
                  <Tabs defaultValue={nestedCards[0].id} className="w-full">
                    <TabsList className="grid grid-cols-3 bg-white/10 dark:bg-gray-800/30">
                      {nestedCards.slice(0, 3).map((card, idx) => (
                        <TabsTrigger 
                          key={card.id} 
                          value={card.id}
                          className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary"
                        >
                          {card.title.length > 10 ? `Tab ${idx + 1}` : card.title}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    {nestedCards.map(card => (
                      <TabsContent key={card.id} value={card.id} className="pt-4">
                        <ActionCard data={card} />
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>
              ) : (
                <div className="pt-4">
                  <ActionCard data={nestedCards[0]} />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Timestamp */}
        <div className="pt-2 text-[10px] text-muted-foreground">
          {data.timestamp}
        </div>
      </Card>
    </motion.div>
  );
};
